import {useEffect, useReducer} from 'react';

import {isComplete} from './fetch';
import IN_MEMORY_CACHE, {type TileMediaStatus} from './fetch/inMemoryCache';
import {type TileMediaSpecKey} from './fetch/keys';

/**
 * Flag-off only: `fetchNewTileMedia` writes `TILE_MEDIA_STATUS_BY_KEY`
 * outside React, so this 100ms poll force-updates `TileMediaLoader` when
 * that map flips to complete.
 *
 * Flag-on (`enabled: false`): Apollo/`useFileData` already re-renders. Do
 * not read the status map when react fetch is enabled.
 */
export function useTileMediaStatusPoll(
  tileMediaSpecKey: TileMediaSpecKey,
  status: TileMediaStatus | null | undefined,
  enabled: boolean
): void {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    if (!enabled || isComplete(status)) {
      return;
    }

    const checkStatus = () => {
      const newStatus =
        IN_MEMORY_CACHE.TILE_MEDIA_STATUS_BY_KEY.get(tileMediaSpecKey);
      if (newStatus && status !== newStatus && isComplete(newStatus)) {
        forceUpdate();
        return false;
      }
      return true;
    };

    const shouldRetry = checkStatus();
    const interval = shouldRetry ? setInterval(checkStatus, 100) : null;
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [enabled, forceUpdate, status, tileMediaSpecKey]);
}
